import { useEffect, useMemo, useState } from "react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getLocalizedDate } from "../../../../../functions/localization";

import { registerPoppins } from "../../../../../fonts/registerFonts";
import PDFHeaderControls from "./PDFHeaderControls";
import PDFPagination from "./PDFPagination";
import PDFReportDocument from "./PDFReportDocument";
import PDFPreviewSheetNotice, {
  usePreviewSheetCount,
} from "../../../pdfReportGeneration/PDFPreviewSheetNotice";
import {
  PDF_PREVIEW_ROWS,
  getPdfPreviewPageCount,
  getPdfTotalsRows,
} from "../../../reportExport";
import {
  computeVariableColumnWidths,
  PDF_PAGE_CONTENT_WIDTH,
} from "../../../pdfReportGeneration/pdfColumnWidths";

registerPoppins();

const MyView = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 20px;
  height: 100%;
  max-height: 1024px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 75px;
  min-height: 75px;
  border-bottom: 1px solid #a7bbcf;
`;

const PDFReportViewer = ({
  user,
  title,
  headings,
  data,
  columnSums,
  columnAverages,
  selectionRange,
  onBack,
  showAVGRow,
  showSUMRow,
  headingsSpacing,
  dataSpacing,
  defaultFontSize,
}) => {
  const { t } = useTranslation("pdfReportViewer");

  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [columnWidthTypeRadioValue, setColumnWidthTypeRadioValue] = useState(1);
  const [fontSizeRadioValue, setFontSizeRadioValue] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 2 : 1,
  );

  const [headingsAndDataFontSize, setHeadingsAndDataFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 8 : 10,
  );
  const [sumAndAvgFontSize, setSumAndAvgFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 10 : 12,
  );

  useEffect(() => {
    if (fontSizeRadioValue === 1) {
      setHeadingsAndDataFontSize(10);
      setSumAndAvgFontSize(12);
    } else {
      setHeadingsAndDataFontSize(8);
      setSumAndAvgFontSize(10);
    }
  }, [fontSizeRadioValue]);

  const handleIsLoading = (boolVal) => {
    setIsLoading(boolVal);
  };

  const allowCustomWidths =
    headingsSpacing?.length === headings.length &&
    dataSpacing?.length === headings.length;

  useEffect(() => {
    if (allowCustomWidths) setColumnWidthTypeRadioValue(3);
  }, []);

  // "Equal Widths": the page's usable width (752pt) shared evenly. JFCRX-7651:
  // this was 760, 8pt more than the page has.
  const columnWidth = PDF_PAGE_CONTENT_WIDTH / headings.length;

  /* JFCRX-7651. Variable Widths used to be sized from the headings alone,
   * then scaled by one factor to fit 758pt: the data scan had been commented
   * out since this file was created (JFCRX-3149-2), so a column with a short
   * heading and long unbreakable values (an NDC, a lot number) was squeezed
   * below its values, and react-pdf drew them into the next column. The scan
   * is restored through the module the Production viewer uses
   * (pdfColumnWidths.js, JFCRX-6733), which pins each column at its longest
   * unbreakable token (up to MAX_CELL_WIDTH, 200pt; a longer token still
   * overlaps, and if even the floors cannot fit, every column is scaled
   * together), sums the widths to the page's 752pt, and measures at the
   * current font size. `data` and the font size are therefore in the
   * dependency list.
   *
   * The rows are passed whole on purpose. Each has 15 values: the first 10
   * are drawn as columns and the last 5 as the detail line below them
   * (PDFReportDocument.js). The module ignores values past the heading count,
   * so the detail-line values do not widen any column. Grouped rows arrive
   * already flattened (JFCRX-6782), so the scan sees the rows the document
   * draws.
   *
   * JFCRX-7652. The Sum and Average rows the download will draw are measured
   * too, at their own font size, so each column also fits its total. The
   * totals arrays have one entry per report field (15); as with the rows,
   * the module ignores the 5 past the heading count, and the document no
   * longer draws them. The preview uses the same widths, so it lays out
   * exactly like the download. */
  const variableColumnWidthsArray = useMemo(
    () =>
      computeVariableColumnWidths({
        headings,
        data,
        fontSize: headingsAndDataFontSize,
        totalsRows: getPdfTotalsRows({
          showSUMRow,
          columnSums,
          showAVGRow,
          columnAverages,
        }),
        totalsFontSize: sumAndAvgFontSize,
      }),
    [
      headings,
      data,
      headingsAndDataFontSize,
      showSUMRow,
      columnSums,
      showAVGRow,
      columnAverages,
      sumAndAvgFontSize,
    ],
  );

  const previewData = useMemo(
    () =>
      data.slice(
        currentPage * PDF_PREVIEW_ROWS,
        (currentPage + 1) * PDF_PREVIEW_ROWS,
      ),
    [data, currentPage],
  );

  /* JFCRX-7643. The preview is one ten-row document per pager page, so
   * react-pdf's own counter inside it always reads "1 / 1". The preview
   * footer states the preview position instead, counted the same way the
   * pager counts. The download passes no label and keeps real page numbers. */
  const previewFooterLabel = t("previewPageLabel", {
    page: currentPage + 1,
    total: getPdfPreviewPageCount(data.length),
  });

  const startDate = selectionRange?.startDate
    ? getLocalizedDate(
        selectionRange.startDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;
  const endDate = selectionRange?.endDate
    ? getLocalizedDate(
        selectionRange.endDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;

  const createDocument = (dataSet, isDownload = false, onSheetCount) => (
    <PDFReportDocument
      title={title}
      headings={headings}
      data={dataSet}
      columnWidth={columnWidth}
      startDate={startDate}
      endDate={endDate}
      variableColumnWidthsArray={variableColumnWidthsArray}
      columnWidthTypeRadioValue={columnWidthTypeRadioValue}
      headingsAndDataFontSize={headingsAndDataFontSize}
      sumAndAvgFontSize={sumAndAvgFontSize}
      columnSums={columnSums}
      columnAverages={columnAverages}
      showSUMRow={showSUMRow}
      showAVGRow={showAVGRow}
      headingsSpacing={headingsSpacing}
      dataSpacing={dataSpacing}
      isDownload={isDownload}
      footerLabel={isDownload ? undefined : previewFooterLabel}
      onSheetCount={isDownload ? undefined : onSheetCount}
    />
  );

  /* JFCRX-7754. The preview document is memoized. PDFViewer lays the PDF out
   * again whenever its child element changes, and a new blob reloads the
   * iframe at the top, so rebuilding it on every render would redo the layout
   * and lose the user's scroll each time the sheet count arrived (it also
   * re-rendered the PDF on every viewer render before this). The dependencies
   * are every input createDocument reads. The download still builds its own
   * document at click time. */
  const { newSheetCounter, sheetsFor } = usePreviewSheetCount();
  const preview = useMemo(() => {
    const counter = newSheetCounter();
    return {
      counter,
      document: createDocument(previewData, false, counter.onSheetCount),
    };
  }, [
    newSheetCounter,
    title,
    headings,
    previewData,
    columnWidth,
    startDate,
    endDate,
    variableColumnWidthsArray,
    columnWidthTypeRadioValue,
    headingsAndDataFontSize,
    sumAndAvgFontSize,
    columnSums,
    columnAverages,
    showSUMRow,
    showAVGRow,
    headingsSpacing,
    dataSpacing,
    previewFooterLabel,
  ]);

  const handleDownload = () => {
    setIsLoading(true);

    // Defer to next event loop tick to allow spinner to render
    setTimeout(async () => {
      try {
        const doc = createDocument(data, true);
        const blobGenerator = pdf([]);
        blobGenerator.updateContainer(doc);
        const blob = await blobGenerator.toBlob();
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `${title.replace(/\s+/g, "_")}_Report.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        setIsLoading(false);
      }
    }, 0); // ← allow render before blocking
  };

  return (
    <MyView>
      <Header>
        <PDFHeaderControls
          t={t}
          title={title}
          onBack={onBack}
          allowCustomWidths={allowCustomWidths}
          columnWidthTypeRadioValue={columnWidthTypeRadioValue}
          setColumnWidthTypeRadioValue={setColumnWidthTypeRadioValue}
          fontSizeRadioValue={fontSizeRadioValue}
          setFontSizeRadioValue={setFontSizeRadioValue}
          onDownload={handleDownload}
          isLoading={isLoading}
          onIsLoading={handleIsLoading}
        />
      </Header>

      <PDFPreviewSheetNotice t={t} sheets={sheetsFor(preview.counter)} />

      {/* JFCRX-7643. The plugin toolbar's page count can only describe the
          ten-row blob on screen, so it is hidden in the preview. */}
      <PDFViewer width="100%" height="86%" showToolbar={false}>
        {preview.document}
      </PDFViewer>

      <PDFPagination
        totalItems={data.length}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={PDF_PREVIEW_ROWS}
      />
    </MyView>
  );
};

export default PDFReportViewer;
